import Anthropic, { APIConnectionError, APIConnectionTimeoutError, APIError, APIUserAbortError } from "@anthropic-ai/sdk";
import type { MessageCreateParamsNonStreaming, Tool } from "@anthropic-ai/sdk/resources/messages";

import type { LlmProviderConfig, LlmModelConfig } from "../../llm/config.js";
import {
  LlmTransportError,
  type LlmAdapter,
  type TextGenerationRequest,
  type TextGenerationResponse,
  type ToolGenerationRequest,
  type ToolGenerationResponse,
  type TokenUsage,
} from "../../llm/types.js";
import { addTokenUsage, withTransientRetry } from "./retry.js";

// Anthropic returns usage.{input_tokens,output_tokens} (+ cache token fields we fold
// into input). Missing → undefined, so accounting counts it as zero rather than erroring.
// Anthropic's native usage.input_tokens is FRESH input only — cache-read and
// cache-creation are separate buckets, NOT included in input_tokens (the opposite of
// OpenAI). So the true total input = input_tokens + both cache buckets, and we surface
// the buckets as subsets of that total to match the shared TokenUsage semantics.
function usageOf(response: { usage?: { input_tokens?: number | null; output_tokens?: number | null; cache_read_input_tokens?: number | null; cache_creation_input_tokens?: number | null } }): TokenUsage {
  const u = response.usage;
  if (!u) return {};
  const cached = u.cache_read_input_tokens ?? 0;
  const created = u.cache_creation_input_tokens ?? 0;
  return {
    inputTokens: (u.input_tokens ?? 0) + cached + created,
    outputTokens: u.output_tokens ?? 0,
    cachedInputTokens: cached,
    cacheCreationTokens: created,
  };
}

function requestError(error: unknown): LlmTransportError {
  if (error instanceof LlmTransportError) return error;
  if (error instanceof APIUserAbortError) return new LlmTransportError("request_failed", "Anthropic request was aborted.");
  if (error instanceof APIConnectionTimeoutError) return new LlmTransportError("timeout", "Anthropic request timed out.", { retryable: true });
  if (error instanceof APIConnectionError) return new LlmTransportError("request_failed", "Anthropic connection failed.", { retryable: true, details: [error.message.slice(0, 500)] });
  if (error instanceof APIError) {
    const status = error.status ?? 0;
    const retryable = status === 408 || status === 429 || status >= 500;
    const code = status === 401 || status === 403 ? "auth" : status === 429 ? "rate_limited" : "request_failed";
    return new LlmTransportError(code, "Anthropic request failed.", { retryable, details: [`status: ${status}`, error.message.slice(0, 500)] });
  }
  const message = error instanceof Error ? error.message : String(error);
  return new LlmTransportError("request_failed", "Anthropic request failed.", { details: [message.slice(0, 500)] });
}

export class AnthropicModel implements LlmAdapter {
  private readonly client: Anthropic;

  constructor(
    private readonly provider: LlmProviderConfig,
    private readonly model: LlmModelConfig,
    apiKey: string,
  ) {
    const options: { apiKey: string; baseURL?: string } = { apiKey };
    if (provider.baseUrl) options.baseURL = provider.baseUrl;
    this.client = new Anthropic({ ...options, maxRetries: 0 });
  }

  private params(request: TextGenerationRequest): MessageCreateParamsNonStreaming {
    const params: MessageCreateParamsNonStreaming = {
      model: this.model.model,
      max_tokens: request.maxOutputTokens,
      stream: false,
      messages: [{ role: "user", content: request.user }],
    };
    if (request.system.trim()) params.system = request.system;
    if (this.model.thinkingBudgetTokens !== undefined) {
      params.thinking = { type: "enabled", budget_tokens: this.model.thinkingBudgetTokens };
    }
    return params;
  }

  async generateText(request: TextGenerationRequest): Promise<TextGenerationResponse> {
    const result = await withTransientRetry(this.provider.maxAttempts, async () => {
      try {
        const response = await this.client.messages.create(this.params(request), {
          timeout: this.provider.timeoutMs,
          signal: request.signal,
        });
        const usage = usageOf(response);
        if (response.stop_reason === "max_tokens") {
          throw new LlmTransportError("truncated", "Provider output was truncated.", { usage });
        }
        if (response.stop_reason === "refusal") throw new LlmTransportError("content_filtered", "Provider filtered the response.", { usage });
        const text = response.content
          .filter((block) => block.type === "text")
          .map((block) => block.text)
          .join("\n")
          .trim();
        if (!text) throw new LlmTransportError("empty_response", "Provider returned an empty text response.", { usage });
        return { text, finishReason: response.stop_reason ?? "", usage, raw: response };
      } catch (error) {
        throw requestError(error);
      }
    });
    return { ...result.value, attempts: result.attempts, usage: addTokenUsage(result.failedUsage, result.value.usage) };
  }

  async generateTool(request: ToolGenerationRequest): Promise<ToolGenerationResponse> {
    const result = await withTransientRetry(this.provider.maxAttempts, async () => {
      try {
        const tool: Tool = {
          name: request.tool.name,
          description: request.tool.description,
          input_schema: { type: "object", ...request.tool.inputSchema },
        };
        const response = await this.client.messages.create({
          ...this.params(request),
          tools: [tool],
          tool_choice: { type: "tool", name: request.tool.name, disable_parallel_tool_use: true },
        }, {
          timeout: this.provider.timeoutMs,
          signal: request.signal,
        });
        const usage = usageOf(response);
        if (response.stop_reason === "max_tokens") {
          throw new LlmTransportError("truncated", "Provider tool output was truncated.", { usage });
        }
        if (response.stop_reason === "refusal") throw new LlmTransportError("content_filtered", "Provider filtered the response.", { usage });
        const block = response.content.find((item) => item.type === "tool_use" && item.name === request.tool.name);
        if (!block || block.type !== "tool_use") {
          throw new LlmTransportError("invalid_response", "Provider did not return the required tool call.", { usage });
        }
        return { input: block.input, finishReason: response.stop_reason ?? "", usage, raw: response };
      } catch (error) {
        throw requestError(error);
      }
    });
    return { ...result.value, attempts: result.attempts, usage: addTokenUsage(result.failedUsage, result.value.usage) };
  }
}
